import {Component, Inject, LOCALE_ID, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {DataService} from '../services/data.service';
import {Utils} from '../services/utils';
import {formatDate} from '@angular/common';
import {TranslateService} from '@ngx-translate/core';
import {environment} from 'src/environments/environment';
import {MatBottomSheet} from '@angular/material/bottom-sheet';
import {MatDialog} from '@angular/material/dialog';
import {AppDownloadBottomSheetComponent} from '../app-download-bottom-sheet/app-download-bottom-sheet.component';
import {ImageCarouselComponent} from '../image-carousel/image-carousel.component';
import {AnalyticsService} from "../analytics.service";

@Component({
  selector: 'app-qr_product-scan',
  templateUrl: './qr-product-scan.component.html',
  styleUrls: ['./qr-product-scan.component.scss']
})
export class QRProductScanComponent implements OnInit {

  scannedProductDetailsResponseBO: any
  productName?: string
  productCategory?: string
  packSize: any
  packSizeUnit?: string
  productInfo: any
  productImage: any
  productType?: string
  mfgDate?: string
  expDate?: string
  uid?: string
  batchNo?: string
  productLabel: any
  directionsForUse: any
  packageOfPractices: string[] = []
  languages: any
  selectedLanguageISO: any
  selectedLanguage: any
  scanTime: any
  playStoreDeepLink?: string
  isManufacturingDateAvailable: Boolean = false
  isExpiryDateAvailable: Boolean = false
  isLabelDetailsAvailable: Boolean = false
  isDirectionsAvailable: Boolean = false
  isPackageOfPracticesAvailable: Boolean = false
  isAppDownloadPopupDisplayed: Boolean = false
  isLanguageSelectionPopupDisplayedFirstTime = false
  displayLanguageSelectionPopup = false;
  pwmRegNum?: string = undefined;
  isPwmRegNumAvailable: boolean = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    public router: Router,
    public dataService: DataService,
    public utils: Utils,
    @Inject(LOCALE_ID) private locale: string,
    public translateService: TranslateService,
    private analyticsService: AnalyticsService,
    private matBottomSheet: MatBottomSheet,
    private dialog: MatDialog) {

    this.selectedLanguageISO = 'en'
    this.dataService.setLanguageISO('')
    this.playStoreDeepLink = environment.API_BASE_URL

    // list of supporting languages with langaugeId and language iso names
    this.languages = dataService.languages

    this.scanTime = formatDate(Date.now(), 'yyyy-MM-dd HH:mm:ss', this.locale);

    this.dataService.displayLangSelPopupObs.subscribe((value) => {
      this.displayLanguageSelectionPopup = value;
    })
    this.dataService.defaultSelectedLanguageObs.subscribe(value => {
      this.selectedLanguage = value;
    })

    this.dataService.languageChangeObs.subscribe(value => {
      if (value) {
        this.validateProduct();
      }
    })
  }

  ngOnInit() {
    this.activatedRoute.paramMap.subscribe(params => {
      let id = params.get('id');

      if (id) {
        const decoded = decodeURIComponent(id);

        // Extract only the first 12-13 digit number using RegEx
        const match = decoded.match(/\b\d{10,16}\b/);
        if (match) {
          const cleanedId = match[0];
          this.dataService.setQRCode(cleanedId);
          console.log("Extracted Product ID:", cleanedId);
        } else {
          console.warn("No valid numeric ID found in:", decoded);
          this.router.navigate(['/error-page']);
        }

      } else if (this.router.url.indexOf("?") > -1) {
        const rawQuery = decodeURIComponent(this.router.url.split('?')[1].replace("=", ''));
        const match = rawQuery.match(/\b\d{10,16}\b/);
        if (match) {
          const cleanedId = match[0];
          this.dataService.setQRCode(cleanedId);
          console.log("Extracted Product ID from Query Param:", cleanedId);
        } else {
          console.warn("No valid numeric ID found in query:", rawQuery);
          this.router.navigate(['/error-page']);
        }
      }
      this.translateService.use(this.selectedLanguageISO);
    });
    if (localStorage.getItem('language_ISO')) {
      this.dataService.setLanguageISO(localStorage.getItem('language_ISO'));
      this.translateService.use(this.dataService.getLanguageISO());
    }
    this.utils.showSpinner()
    this.validateProduct()

  }

  showLanguageSelectionPopup() {
    this.dataService.defaultSelectedLanguageObs.subscribe(value => {
      this.selectedLanguage = value;
    })
    this.displayLanguageSelectionPopup = true;
  }

  public showLabelDetails() {
    this.analyticsService.logEvent('label');
    window.open(this.productLabel, "label");
  }

  public showDirectionsForUse() {
    this.analyticsService.logEvent("dfu");

    // Prepare images array
    let images: string[] = [];

    if (Array.isArray(this.directionsForUse)) {
      images = this.directionsForUse;
    } else if (this.directionsForUse) {
      images = [this.directionsForUse];
    }

    if (images.length === 0) {
      console.warn("No directions for use available.");
      return;
    }

    // Check if mobile device
    const isMobile = window.innerWidth <= 768;

    // Open the carousel dialog with responsive configuration
    this.dialog.open(ImageCarouselComponent, {
      width: isMobile ? '100vw' : '90vw',
      maxWidth: isMobile ? '100vw' : '1200px',
      height: isMobile ? '100vh' : 'auto',
      maxHeight: isMobile ? '100vh' : '90vh',
      panelClass: 'carousel-dialog-container',
      autoFocus: false,
      restoreFocus: false,
      data: {
        images: images,
        productName: this.productName || 'Product',
        title: 'Directions for Use'
      }
    });
  }

  public showPackageOfPractices() {
    this.analyticsService.logEvent("pop");

    // Prepare images array
    let images: string[] = [];

    if (Array.isArray(this.packageOfPractices)) {
      images = this.packageOfPractices;
    } else if (this.packageOfPractices) {
      images = [this.packageOfPractices as any];
    }

    if (images.length === 0) {
      console.warn("No package of practices available or invalid data.");
      return;
    }

    // Check if mobile device
    const isMobile = window.innerWidth <= 768;

    // Open the carousel dialog with responsive configuration
    this.dialog.open(ImageCarouselComponent, {
      width: isMobile ? '100vw' : '90vw',
      maxWidth: isMobile ? '100vw' : '1200px',
      height: isMobile ? '100vh' : 'auto',
      maxHeight: isMobile ? '100vh' : '90vh',
      panelClass: 'carousel-dialog-container',
      autoFocus: false,
      restoreFocus: false,
      data: {
        images: images,
        productName: this.productName || 'Product',
        title: 'Package of Practices'
      }
    });
  }

  private validateProduct() {
    this.analyticsService.logEvent("language_change", {
      'language_selected': this.dataService.getLanguageISO()
    });

    this.dataService.validateProduct(this.dataService.getLanguageISO(), this.scanTime, "185.221.69.47").subscribe(async data => {
      this.utils.hideSpinner()
      this.scannedProductDetailsResponseBO = data;

      //check whether productInfo is null
      if (this.scannedProductDetailsResponseBO.data.productInfo != null) {
        this.productName = this.scannedProductDetailsResponseBO.data.productInfo.productName
      } else {
        this.productName = this.scannedProductDetailsResponseBO.data.qrCodeInfo.productName
      }

      if (this.scannedProductDetailsResponseBO.data.productInfo != null) {
        this.productCategory = this.scannedProductDetailsResponseBO.data.productInfo.productCategory
      }

      if (this.scannedProductDetailsResponseBO.data.productInfo != null) {
        this.productImage = this.scannedProductDetailsResponseBO.data.productInfo.productImage
      } else {
        this.productImage = 'assets/logo_bayer.svg'
      }

      this.packSize = this.scannedProductDetailsResponseBO.data.qrCodeInfo.packSize
      this.packSizeUnit = this.scannedProductDetailsResponseBO.data.qrCodeInfo.packSizeUnit
      this.uid = this.scannedProductDetailsResponseBO.data.qrCodeInfo.UID
      this.batchNo = this.scannedProductDetailsResponseBO.data.qrCodeInfo.batchNo

      if (this.scannedProductDetailsResponseBO.data.qrCodeInfo != null &&
        this.scannedProductDetailsResponseBO.data.qrCodeInfo.mfgDate != null &&
        this.scannedProductDetailsResponseBO.data.productInfo != null) {
        if (this.scannedProductDetailsResponseBO.data.productInfo.productType == "SeedProduct") {
          this.productType = "date_of_testing"
        } else {
          this.productType = "manufacturing_date"
        }
        this.isManufacturingDateAvailable = true
        this.mfgDate = this.scannedProductDetailsResponseBO.data.qrCodeInfo.mfgDate
      }

      if (this.scannedProductDetailsResponseBO.data.qrCodeInfo != null &&
        this.scannedProductDetailsResponseBO.data.qrCodeInfo.expDate != null) {
        this.isExpiryDateAvailable = true
        this.expDate = this.scannedProductDetailsResponseBO.data.qrCodeInfo.expDate
      }

      if (this.scannedProductDetailsResponseBO.data.cpProductInfo != null
        && this.scannedProductDetailsResponseBO.data.cpProductInfo.productLabel != null) {
        this.productLabel = this.scannedProductDetailsResponseBO.data.cpProductInfo.productLabel[0];
        this.isLabelDetailsAvailable = true
      }

      if (this.scannedProductDetailsResponseBO.data.cpProductInfo != null
        && this.scannedProductDetailsResponseBO.data.cpProductInfo.directionForUse != null) {
        this.directionsForUse = this.scannedProductDetailsResponseBO.data.cpProductInfo.directionForUse;
        this.isDirectionsAvailable = true
      }

      if (this.scannedProductDetailsResponseBO.data.productInfo != null
        && this.scannedProductDetailsResponseBO.data.productInfo.packageOfPractices != null
        && this.scannedProductDetailsResponseBO.data.productInfo.packageOfPractices.length > 0) {
        this.packageOfPractices = this.scannedProductDetailsResponseBO.data.productInfo.packageOfPractices;
        this.isPackageOfPracticesAvailable = true
      }

      if (this.scannedProductDetailsResponseBO.data.language_ISO != null) {
        let data = JSON.parse(JSON.stringify(this.scannedProductDetailsResponseBO.data))
        this.dataService.languages.forEach((lang: { iso6391: any; name: String; }) => {
          if (lang.iso6391 == data.language_ISO) {
            this.dataService.defaultSelectedLanguage.next(lang.name)
            this.dataService.setLanguageISO(lang.iso6391)
            this.translateService.use(lang.iso6391)
          }
        });
      }

      if (!this.isLanguageSelectionPopupDisplayedFirstTime) {
        setTimeout(() => {
          if (!localStorage.getItem('language_ISO')) {
            this.isLanguageSelectionPopupDisplayedFirstTime = true;
            this.displayLanguageSelectionPopup = true;
          }
        }, 1000);
      }

      this.dataService.languageChange.next(false);

      if( this.scannedProductDetailsResponseBO.data.pwmRegNum != null){
        this.pwmRegNum= this.scannedProductDetailsResponseBO.data.pwmRegNum;
        this.isPwmRegNumAvailable = true;
      }

      this.analyticsService.logEvent("screen_view_scan", {
        'product_name': this.productName,
        'product_category': this.productCategory,
        'pack_size': this.packSize,
        'pack_sizeUnit': this.packSizeUnit,
        'label_available': this.isLabelDetailsAvailable,
        'dfu_available': this.isDirectionsAvailable,
        'pop_available': this.isPackageOfPracticesAvailable
      })
    });
  }

  public displayAppDownloadPopup() {
    if (!this.isAppDownloadPopupDisplayed && localStorage.getItem('isAppDownloadPopupDisplayed') != "true"
    && (window.navigator.appVersion.split(';')[1].trim().split(' ')[0].trim().toLowerCase() === 'android')) {
      setTimeout(() => {
        this.isAppDownloadPopupDisplayed = true
        localStorage.setItem('isAppDownloadPopupDisplayed', "true")
        this.matBottomSheet.open(AppDownloadBottomSheetComponent);
      }, 5000)
    }
  }
  public openMobileApp(bannerImage: string) {
    this.analyticsService.logEvent(bannerImage)
    window.open(environment.appDownloadCardDynamicLink, "appDownloadCardDynamicLink");
  }
}
